import { useEffect } from 'react';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface PageMetaProps {
  title: string;
  description: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  breadcrumbs?: BreadcrumbItem[];
}

const BASE_URL = 'https://listbright.ai';
const DEFAULT_OG_IMAGE = `${BASE_URL}/logo.png`;

export function PageMeta({
  title,
  description,
  canonical,
  ogType = 'website',
  ogImage,
  noindex = false,
  jsonLd,
  breadcrumbs,
}: PageMetaProps) {
  useEffect(() => {
    // Title
    const fullTitle = title.includes('ListBright') ? title : `${title} | ListBright`;
    document.title = fullTitle;

    // Helper to set or create a meta tag
    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // Standard meta
    setMeta('name', 'description', description);
    if (noindex) {
      setMeta('name', 'robots', 'noindex, nofollow');
    } else {
      setMeta('name', 'robots', 'index, follow');
    }

    // Canonical
    const canonicalUrl = canonical || `${BASE_URL}${window.location.pathname}`;
    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canonicalUrl);

    // Open Graph
    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:image', ogImage || DEFAULT_OG_IMAGE);
    setMeta('property', 'og:site_name', 'ListBright');

    // The static og:image dimensions in index.html describe the default logo;
    // drop them when a page supplies its own image so they don't mismatch.
    if (ogImage && ogImage !== DEFAULT_OG_IMAGE) {
      document
        .querySelectorAll(
          'meta[property="og:image:width"], meta[property="og:image:height"], meta[property="og:image:alt"], meta[name="twitter:image:alt"]'
        )
        .forEach((el) => el.remove());
    } else {
      setMeta('property', 'og:image:width', '1080');
      setMeta('property', 'og:image:height', '589');
      setMeta('property', 'og:image:alt', 'ListBright logo');
      setMeta('name', 'twitter:image:alt', 'ListBright logo');
    }

    // Twitter Card
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', fullTitle);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', ogImage || DEFAULT_OG_IMAGE);

    // JSON-LD
    // Remove any previously injected page-level JSON-LD
    document.querySelectorAll('script[data-page-jsonld]').forEach((el) => el.remove());
    // Collect all JSON-LD items
    const allJsonLd: Record<string, unknown>[] = [];

    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      allJsonLd.push(...items);
    }

    // Auto-generate BreadcrumbList from breadcrumbs prop
    if (breadcrumbs && breadcrumbs.length > 0) {
      const breadcrumbList = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
          ...breadcrumbs.map((crumb, i) => ({
            '@type': 'ListItem',
            position: i + 2,
            name: crumb.label,
            ...(crumb.path ? { item: `${BASE_URL}${crumb.path}` } : {}),
          })),
        ],
      };
      allJsonLd.push(breadcrumbList);
    }

    allJsonLd.forEach((item) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-page-jsonld', 'true');
      script.textContent = JSON.stringify(item);
      document.head.appendChild(script);
    });

    return () => {
      // Cleanup JSON-LD on unmount
      document.querySelectorAll('script[data-page-jsonld]').forEach((el) => el.remove());
    };
  }, [title, description, canonical, ogType, ogImage, noindex, jsonLd, breadcrumbs]);

  return null;
}
